import type { Client, Options, Target } from '@harnessio/ff-nodejs-server-sdk';
import type { Result as BrowserClient } from '@harnessio/ff-javascript-client-sdk';
import { FEATURE_FLAGS, featureFlagDefinitions, FLAG_TYPE } from './interfaces';

class ClientManager {
  private clients: Map<string, Client> = new Map();
  private pendingClients: Map<string, Promise<Client | null>> = new Map();
  private static instance: ClientManager | null = null;

  private constructor() {}

  static getInstance(): ClientManager {
    if (!ClientManager.instance) {
      ClientManager.instance = new ClientManager();
    }
    return ClientManager.instance;
  }

  async getClient(apiKey: string, options?: Options): Promise<Client | null> {
    // Return existing client if already created
    if (this.clients.has(apiKey)) {
      return this.clients.get(apiKey)!;
    }

    // If client creation is already in progress, wait for it
    if (this.pendingClients.has(apiKey)) {
      return await this.pendingClients.get(apiKey)!;
    }

    // Create new client if it doesn't exist and isn't being created
    const clientPromise = this.createClient(apiKey, options || {});
    this.pendingClients.set(apiKey, clientPromise);

    try {
      const client = await clientPromise;
      return client;
    } finally {
      // Clean up the pending promise regardless of success/failure
      this.pendingClients.delete(apiKey);
    }
  }

  private async createClient(apiKey: string, options: Options): Promise<Client | null> {
    // Double-check if client was created while we were waiting
    if (this.clients.has(apiKey)) {
      return this.clients.get(apiKey)!;
    }

    try {
      if (typeof window === 'undefined') {
        const ServerClient = (await import('@harnessio/ff-nodejs-server-sdk')).Client;
        const client = new ServerClient(apiKey, options) as Client;

        await client.waitForInitialization();
        this.clients.set(apiKey, client);

        return client;
      }
      return null;
    } catch (err) {
      console.error(`Error initializing NodeJS SDK client:`, err);
      return null;
    }
  }

  // Additional utility methods
  hasClient(apiKey: string): boolean {
    return this.clients.has(apiKey);
  }

  isClientPending(apiKey: string): boolean {
    return this.pendingClients.has(apiKey);
  }

  async removeClient(apiKey: string): Promise<boolean> {
    const client = this.clients.get(apiKey);
    if (client) {
      try {
        await client.close();
      } catch (err) {
        console.error(`Error closing client:`, err);
      }
    }

    // Also remove any pending client creation
    this.pendingClients.delete(apiKey);
    return this.clients.delete(apiKey);
  }

  getAllClients(): Map<string, Client> {
    return new Map(this.clients);
  }

  getClientCount(): number {
    return this.clients.size;
  }

  getPendingClientCount(): number {
    return this.pendingClients.size;
  }

  // Clean up all clients (useful for testing or shutdown)
  async clearAllClients(): Promise<void> {
    // Wait for any pending clients to complete first
    const pendingPromises = Array.from(this.pendingClients.values());
    await Promise.allSettled(pendingPromises);

    const closePromises = Array.from(this.clients.values()).map(async client => {
      try {
        await client.close();
      } catch (err) {
        console.error('Error closing client during cleanup:', err);
      }
    });

    await Promise.allSettled(closePromises);
    this.clients.clear();
    this.pendingClients.clear();
  }
}

class FeatureFlagService {
  private client: Client | undefined;
  private browserClient: BrowserClient | undefined;
  private isServer: boolean = typeof window === 'undefined';
  private apiKey: string;
  private options: Options;
  private clientInitFailure: boolean = false;
  private defaultOptions: Options = {
    enableStream: true,
    enableAnalytics: true,
  };

  constructor(apiKey: string, options?: Options) {
    this.apiKey = apiKey;
    this.options = {
      ...this.defaultOptions,
      ...options,
    };

    if (typeof window === 'undefined') {
      try {
        import('fs').then(fsmod => {
          import('path').then(pathmod => {
            const __crtPath = pathmod.resolve(__dirname, './ca.crt');
            fsmod.writeFileSync(__crtPath, process.env['relay-cert.pem'] || '');
            if (process.env['NODE_ENV'] !== 'development' && this.isServer) {
              this.options = {
                baseUrl: process.env['HARNESS_RELAY_URL'],
                eventsUrl: process.env['HARNESS_RELAY_URL'],
                tlsTrustedCa: __crtPath,
                ...this.options,
              };
            }
          });
        });
      } catch (err) {
        console.log(err);
      }
    }
  }

  private handlerBrowserInitError(reject: (reason?: unknown) => void, error: unknown) {
    this.clientInitFailure = true;
    console.log(error);
    reject(error);
  }

  private async initBrowserClient(target: Target = { identifier: 'default', name: 'default' }) {
    // client execution
    const { initialize, Event } = await import('@harnessio/ff-javascript-client-sdk');
    this.browserClient = initialize(this.apiKey, target);
    return new Promise((resolve, reject) => {
      if (!this.browserClient) {
        this.clientInitFailure = true;
      }
      // Event happens when connection to server is established
      this.browserClient?.on(Event.READY, () => resolve(true));

      this.browserClient?.on(Event.ERROR, error => this.handlerBrowserInitError(reject, error)); // Event happens when connection some error has occurred
      this.browserClient?.on(Event.ERROR_AUTH, error => this.handlerBrowserInitError(reject, error)); // Event happens when unable to authenticate
      this.browserClient?.on(Event.ERROR_FETCH_FLAGS, error => this.handlerBrowserInitError(reject, error)); // Event happens when unable to fetch flags from the service
    }).catch(console.log);
  }

  private async initClient(target: Target = { identifier: 'default', name: 'default' }) {
    // server execution
    try {
      if (typeof window === 'undefined') {
        const client = await ClientManager.getInstance().getClient(this.apiKey, this.options);
        if (!client) {
          this.clientInitFailure = true;
          return;
        }
        this.client = client;
        return;
      }
    } catch (err) {
      this.clientInitFailure = true;
      return;
    }
    this.initBrowserClient(target);
  }

  async close() {
    if (typeof window === 'undefined') {
      await this.client?.close();
      return;
    }
    this.browserClient?.close();
  }

  private evaluateClientFlag(id: FEATURE_FLAGS, type: FLAG_TYPE) {
    switch (type) {
      case FLAG_TYPE.BOOLEAN:
        return this.browserClient?.variation(id, false) || false;
      case FLAG_TYPE.STRING:
        return this.browserClient?.variation(id, '') || '';
      case FLAG_TYPE.JSON:
        return this.browserClient?.variation(id, {}) || {};
    }
  }

  private async evaluateServerFlag(id: FEATURE_FLAGS, type: FLAG_TYPE, target?: Target) {
    switch (type) {
      case FLAG_TYPE.BOOLEAN:
        return (await this.client?.boolVariation?.(id, target, false)) || Promise.resolve(false);
      case FLAG_TYPE.STRING:
        return (await this.client?.stringVariation?.(id, target, '')) || Promise.resolve('');
      case FLAG_TYPE.JSON:
        return (await this.client?.jsonVariation?.(id, target, {})) || Promise.resolve({});
    }
  }

  private async evaluateFlag(id: FEATURE_FLAGS, type: FLAG_TYPE, target?: Target) {
    if (this.isServer) {
      return this.evaluateServerFlag(id, type, target);
    }
    return this.evaluateClientFlag(id, type) as Promise<string | boolean | Record<string, unknown>>;
  }

  async getFlags(flags: FEATURE_FLAGS[], { target }: { target?: Target }) {
    if (!this.client || this.clientInitFailure) {
      await this.initClient();
    }
    const result: { [key in FEATURE_FLAGS]?: string | boolean | Record<string, unknown> } = {};
    for (const flag of flags) {
      const featureFlagDefinition = featureFlagDefinitions[flag];
      if (!featureFlagDefinition) {
        result[flag] = false;
      } else {
        result[flag] = await this.evaluateFlag(featureFlagDefinition.id, featureFlagDefinition.type, target);
      }
    }
    return result;
  }

  async getAllFlags({ target }: { target: Target }) {
    return this.getFlags(Object.values(FEATURE_FLAGS) as FEATURE_FLAGS[], { target });
  }
}

export { FeatureFlagService };
